import {useCallback, type ChangeEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {ICO_SIZES, IMAGE_FORMATS, type ImageFormat} from '../../../common/constants/converterFormats'
import {formatBytes} from '../../../common/utils/formatBytes'

interface ImageFormatSelectorProps {
  format: ImageFormat
  quality: number
  icoSizes: ReadonlySet<number>
  /** Real-encoded size for the current settings (see ImageConverterPage.tsx's
   * debounced estimate effect), or null while unavailable/still computing. */
  estimatedSizeBytes: number | null
  onFormatChange: (format: ImageFormat) => void
  onQualityChange: (quality: number) => void
  onIcoSizeToggle: (size: number, enabled: boolean) => void
}

const ImageFormatSelector = ({
  format,
  quality,
  icoSizes,
  estimatedSizeBytes,
  onFormatChange,
  onQualityChange,
  onIcoSizeToggle,
}: ImageFormatSelectorProps) => {
  const {t} = useTranslation()

  const handleQualityChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onQualityChange(Number(e.target.value) / 100),
    [onQualityChange],
  )

  const meta = IMAGE_FORMATS.find((f) => f.id === format)

  return (
    <div className="imc-format">
      <div className="imc-format-options">
        {IMAGE_FORMATS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={f.id === format ? 'imc-format-btn is-active' : 'imc-format-btn'}
            onClick={() => onFormatChange(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {meta?.supportsQuality && (
        <div className="imc-slider">
          <span className="imc-slider-label">{t('imageConverter.format.quality')}</span>
          <input
            type="range"
            min={60}
            max={100}
            step={1}
            value={Math.round(quality * 100)}
            onChange={handleQualityChange}
          />
          <span className="imc-slider-value">{Math.round(quality * 100)}%</span>
        </div>
      )}

      {format === 'ico' && (
        <div className="imc-ico-sizes">
          <span className="imc-slider-label">{t('imageConverter.format.icoSizes')}</span>
          <div className="imc-ico-size-grid">
            {ICO_SIZES.map((size) => (
              <label key={size} className="imc-toggle">
                <input
                  type="checkbox"
                  checked={icoSizes.has(size)}
                  onChange={(e) => onIcoSizeToggle(size, e.target.checked)}
                />
                {size}px
              </label>
            ))}
          </div>
        </div>
      )}

      {format === 'gif' && <p className="cvt-hint">{t('imageConverter.format.gifHint')}</p>}

      {estimatedSizeBytes !== null && (
        <p className="imc-estimated-size">
          {t('imageConverter.format.estimatedSize')} <strong>{formatBytes(estimatedSizeBytes)}</strong>
        </p>
      )}
    </div>
  )
}

export default ImageFormatSelector
